import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './Layout';
import { authFetch } from '../../utils/auth';
import { toast } from 'react-toastify';
import {
  Users,
  UserPlus,
  Search,
  Download,
  Trash2,
  Shield,
  User,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Filter,
} from 'lucide-react';
import CreateUserModal from './CreateUserModal';

const ROLES = ['all', 'admin', 'user'];
const STATUS = ['all', 'active', 'inactive'];

const UserManagement = () => {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [roleFilter, setRole]     = useState('all');
  const [statusFilter, setStatus] = useState('all');
  const [page, setPage]           = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (roleFilter !== 'all') params.set('role', roleFilter);
      if (statusFilter !== 'all') params.set('isActive', statusFilter === 'active');

      const res = await authFetch(`/api/users?${params.toString()}`);
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        setUsers(data.data);
        setPagination(data.pagination);
      } else {
        toast.error(data.message || 'Failed to load users');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (id) => {
    try {
      const res = await authFetch(`/api/users/${id}`, { method: 'DELETE' });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        toast.success('User deactivated');
        fetchUsers();
      } else {
        toast.error(data.message || 'Failed to deactivate user');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleExportAll = async () => {
    try {
      const res = await authFetch('/api/users/export/csv');
      if (!res) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  // Client-side search filter (on top of server-side role/status)
  const filtered = search.trim()
    ? users.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.username?.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const avatar = (user) => {
    if (user.photoId) {
      return (
        <img
          src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/${user._id}/photo`}
          alt={user.name}
          className="w-9 h-9 rounded-full object-cover"
        />
      );
    }
    return (
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
        style={{ background: 'linear-gradient(135deg,#FF9B51,#e07a33)' }}
      >
        {user.name?.[0]?.toUpperCase() || <User className="w-4 h-4" />}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-5">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#25343F' }}>User Management</h1>
            <p className="text-sm mt-0.5" style={{ color: '#BFC9D1' }}>
              {pagination.total} total user{pagination.total !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportAll}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors hover:bg-white"
              style={{ borderColor: '#BFC9D1', color: '#25343F', backgroundColor: 'transparent' }}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-accent transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#FF9B51' }}
            >
              <UserPlus className="w-4 h-4" />
              New User
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border p-4 flex flex-col sm:flex-row gap-3" style={{ borderColor: '#BFC9D1' }}>
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#BFC9D1' }} />
            <input
              type="text"
              placeholder="Search by name, email, username…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none transition-all"
              style={{ borderColor: '#BFC9D1', backgroundColor: '#EAEFEF', color: '#25343F' }}
              onFocus={e => { e.target.style.borderColor = '#FF9B51'; e.target.style.boxShadow = '0 0 0 3px rgba(255,155,81,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = '#BFC9D1'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 flex-shrink-0" style={{ color: '#BFC9D1' }} />
            {/* Role */}
            <select
              value={roleFilter}
              onChange={e => { setRole(e.target.value); setPage(1); }}
              className="px-3 py-2.5 rounded-xl border text-sm outline-none"
              style={{ borderColor: '#BFC9D1', backgroundColor: '#EAEFEF', color: '#25343F' }}
            >
              {ROLES.map(r => <option key={r} value={r}>{r === 'all' ? 'All roles' : r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
            {/* Status */}
            <select
              value={statusFilter}
              onChange={e => { setStatus(e.target.value); setPage(1); }}
              className="px-3 py-2.5 rounded-xl border text-sm outline-none"
              style={{ borderColor: '#BFC9D1', backgroundColor: '#EAEFEF', color: '#25343F' }}
            >
              {STATUS.map(s => <option key={s} value={s}>{s === 'all' ? 'All status' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            <button onClick={fetchUsers} className="p-2.5 rounded-xl border hover:bg-gray-50 transition-colors" style={{ borderColor: '#BFC9D1' }}>
              <RefreshCw className="w-4 h-4" style={{ color: '#BFC9D1' }} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border shadow-brand" style={{ borderColor: '#BFC9D1', overflow: 'hidden' }}>
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-transparent" style={{ borderColor: '#FF9B51', borderTopColor: 'transparent' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-10 h-10 mx-auto mb-3" style={{ color: '#BFC9D1' }} />
              <p className="font-medium" style={{ color: '#25343F' }}>No users found</p>
              <p className="text-sm mt-1" style={{ color: '#BFC9D1' }}>Try adjusting the filters or create a new user.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '680px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#EAEFEF', borderBottom: '1px solid #BFC9D1' }}>
                    {['User', 'Email', 'Phone', 'Role', 'Status', 'Created', 'Actions'].map(h => (
                      <th key={h} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#25343F' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ '--tw-divide-opacity': 1, borderColor: '#EAEFEF' }}>
                  {filtered.map(user => (
                    <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                      {/* User */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {avatar(user)}
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate" style={{ color: '#25343F' }}>{user.name}</p>
                            <p className="text-xs" style={{ color: '#BFC9D1' }}>@{user.username}</p>
                          </div>
                        </div>
                      </td>
                      {/* Email */}
                      <td className="px-5 py-4">
                        <span className="text-sm" style={{ color: '#25343F' }}>{user.email}</span>
                      </td>
                      {/* Phone */}
                      <td className="px-5 py-4">
                        <span className="text-sm" style={{ color: '#BFC9D1' }}>{user.phone || '—'}</span>
                      </td>
                      {/* Role */}
                      <td className="px-5 py-4">
                        <span
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold capitalize"
                          style={user.role === 'admin'
                            ? { backgroundColor: 'rgba(37,52,63,0.1)', color: '#25343F' }
                            : { backgroundColor: 'rgba(255,155,81,0.12)', color: '#FF9B51' }
                          }
                        >
                          {user.role === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                          {user.role}
                        </span>
                      </td>
                      {/* Status */}
                      <td className="px-5 py-4">
                        <span
                          className="px-2.5 py-1 rounded-full text-xs font-semibold"
                          style={user.isActive
                            ? { backgroundColor: '#dcfce7', color: '#166534' }
                            : { backgroundColor: '#EAEFEF', color: '#BFC9D1' }
                          }
                        >
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {/* Created */}
                      <td className="px-5 py-4">
                        <span className="text-xs" style={{ color: '#BFC9D1' }}>
                          {new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </td>
                      {/* Actions */}
                      <td className="px-5 py-4">
                        <button
                          onClick={() => setDeleteTarget(user)}
                          className="p-2 rounded-lg transition-colors hover:bg-red-50 text-red-400 hover:text-red-600"
                          title="Deactivate user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: '#BFC9D1' }}>
              Page {page} of {pagination.pages} — {pagination.total} users
            </p>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="p-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-white transition-colors"
                style={{ borderColor: '#BFC9D1' }}
              >
                <ChevronLeft className="w-4 h-4" style={{ color: '#25343F' }} />
              </button>
              <button
                disabled={page >= pagination.pages}
                onClick={() => setPage(p => p + 1)}
                className="p-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-white transition-colors"
                style={{ borderColor: '#BFC9D1' }}
              >
                <ChevronRight className="w-4 h-4" style={{ color: '#25343F' }} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showModal && (
        <CreateUserModal
          onClose={() => setShowModal(false)}
          onCreated={() => { fetchUsers(); setShowModal(false); }}
        />
      )}

      {/* Delete Confirm Dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-brand p-6 max-w-sm w-full" style={{ borderColor: '#BFC9D1' }}>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#25343F' }}>Deactivate User?</h3>
            <p className="text-sm mb-6" style={{ color: '#BFC9D1' }}>
              <strong style={{ color: '#25343F' }}>{deleteTarget.name}</strong> will be deactivated and cannot log in.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium transition-colors hover:bg-gray-50"
                style={{ borderColor: '#BFC9D1', color: '#25343F' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteTarget._id)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default UserManagement;
